// src/pages/university-admin/InstitutionAdminDashboard.js

import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Grid, 
    Paper, 
    Typography, 
    Box, 
    Card, 
    CardContent, 
    Divider, 
    useTheme, 
    Button, 
    useMediaQuery,
    Chip,
    IconButton
} from '@mui/material';
import { 
    SupervisorAccount, 
    People, 
    CheckCircle, 
    Refresh as RefreshIcon,
    AssignmentInd,
    HowToReg,
    Analytics,
    Domain,
    ArrowForward
} from '@mui/icons-material';
import { apiService } from '../../apiServices/ApiService';
import { APIInstitutionAdminDashboard } from '../../apiServices/APIs';
import { getUserDetails } from '../../utils/helpers';
import LoadingState from '../../components/LoadingState';
// NOTE: The component file is still named InstitutionAdminBar.js
// but it now renders "Unit Research Lead Portal" as its visible title.
import InstitutionAdminBar from '../../components/InstitutionAdminBar';
import { useNavigate } from 'react-router-dom';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const StatCard = ({ title, value, subtext, icon, color }) => (
    <Card sx={{ height: '100%', borderRadius: 3, borderLeft: `6px solid ${color}`, boxShadow: 1 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 2, bgcolor: `${color}15`, borderRadius: '50%', color: color }}>
                {icon}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{value}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>{title}</Typography>
                {subtext && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        {subtext}
                    </Typography>
                )}
            </Box>
        </CardContent>
    </Card>
);

const InstitutionAdminDashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total_users: 0,
        total_students: 0,
        total_supervisors: 0,
        total_assignments: 0,
        pending_student_approvals: 0,
        pending_supervisor_approvals: 0
    });

    const adminId = getUserDetails("Id");
    const universityId = getUserDetails("universityId");

    const fetchDashboardStats = async () => {
        setLoading(true);
        try {
            // Fetch stats using structural path format: API/{adminId}/{universityId}
            const response = await apiService.listDashboardData(
                APIInstitutionAdminDashboard,
                `${adminId}/${universityId}`
            );
            if (response.success && response.data) {
                setStats(response.data);
            }
        } catch (error) {
            console.error("Unit research lead dashboard fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (adminId && universityId) {
            fetchDashboardStats();
        }
    }, [adminId, universityId]);

    if (loading) {
        return <LoadingState message="Loading dashboard data..." />;
    }

    return (
        <FullWidthContainer >
            <Box sx={{ backgroundColor: 'white', borderRadius: 3, mb: 3 }}>
                <InstitutionAdminBar notificationCount={12} />

                {/* Welcome Header */}
                <Paper elevation={1} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    borderRadius: 3, 
                    color: theme.palette.primary.main
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Welcome, {getUserDetails("userName") || 'Unit Research Lead'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                                <Domain sx={{ fontSize: 16 }} /> {getUserDetails("university") || 'Institution Portal'}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton 
                                size="small" 
                                onClick={fetchDashboardStats} 
                                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
                            >
                                <RefreshIcon size="small" />
                            </IconButton>
                            <Chip 
                                label="System Active" 
                                color="success" 
                                size="medium" 
                                variant="outlined" 
                                icon={<CheckCircle />} 
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* Metrics Dashboard Grid */}
            <Grid container spacing={3}>
                {/* Total Users Stat Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Registered Users" 
                        value={stats.total_users} 
                        subtext={`${stats.total_students} Students • ${stats.total_supervisors} Supervisors`}
                        icon={<People />} 
                        color={theme.palette.primary.main} 
                    />
                </Grid>

                {/* Assign Supervisor Stat Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Supervisor Assignments" 
                        value={stats.total_assignments} 
                        subtext="Assigned Students"
                        icon={<AssignmentInd />} 
                        color={theme.palette.secondary.main} 
                    />
                </Grid>

                {/* Student Approvals Stat Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Student Approvals" 
                        value={stats.pending_student_approvals} 
                        subtext="Pending Verification"
                        icon={<HowToReg />} 
                        color={theme.palette.warning.main} 
                    />
                </Grid>

                {/* Supervisor Approvals Stat Card */}
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Supervisor Approvals" 
                        value={stats.pending_supervisor_approvals} 
                        subtext="Pending Verification"
                        icon={<SupervisorAccount />} 
                        color={theme.palette.info.main} 
                    />
                </Grid>
            </Grid>

            {/* Quick Actions Panel */}
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Analytics color="primary" /> Administrative Overview
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Typography color="text.secondary" paragraph>
                            Review metrics regarding users, academic assignments, and accounts awaiting administrative actions. 
                            Ensure that pending verification lists are resolved timely to enable research progress.
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>
        </FullWidthContainer>
    );
};

export default InstitutionAdminDashboard;